import { useState, useMemo, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { templates, categories } from '@/mocks/templates';

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const activeCategory = useMemo(() => {
    return categories.find((c) => c.slug === slug) || categories[0];
  }, [slug]);

  const filteredTemplates = useMemo(() => {
    if (slug === 'all') return templates;
    return templates.filter((t) => t.categorySlug === slug);
  }, [slug]);

  const handleCategoryChange = useCallback((newSlug: string) => {
    navigate(newSlug === 'all' ? '/category/all' : `/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigate]);

  return (
    <>
      <main className="pt-16 min-h-screen bg-background-50">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '模板中心', path: '/category/all' },
            ...(slug !== 'all' ? [{ label: activeCategory.name }] : []),
          ]}
        />

        <section className="w-full px-4 md:px-6 py-10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-heading text-foreground-900 mb-4">
                网站模板中心
              </h1>
              <p className="text-base text-foreground-600 max-w-2xl mx-auto">
                2000+精美模板，覆盖全行业需求。选择你喜欢的设计，一键开始建站。
              </p>
            </div>

            <div className="flex items-center justify-center gap-2 flex-wrap mb-12">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 text-sm rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:text-primary-500 hover:bg-primary-50 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {filteredTemplates.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-file-search-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无模板</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop="">
                {filteredTemplates.map((tpl) => (
                  <div
                    key={tpl.id}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={tpl.image}
                        alt={tpl.title}
                        title={`${tpl.title} - 免费在线建站模板`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <span className="inline-block px-2.5 py-1 text-xs rounded-full bg-accent-100 text-accent-700 mb-3">
                        {tpl.category}
                      </span>
                      <h3 className="text-base font-heading text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors duration-200">
                        {tpl.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                        {tpl.description}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {tpl.features.slice(0, 3).map((f) => (
                          <span key={f} className="text-xs px-2 py-0.5 rounded bg-secondary-100 text-secondary-700">
                            {f}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}